import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    FaArrowRight,
    FaSearch,
    FaSort,
    FaSortDown,
    FaSortUp,
    FaSync,
    FaMagic,
    FaTimes,
} from 'react-icons/fa';
import FlashcardOptionsModal from './FlashcardOptionsModal';
import PointsConfirmationModal from './PointsConfirmationModal';
import { regenerateFlashcards } from './FileUploader';
import { QUIZMASTER_API } from '../config';
import { formatToLocalDateTime, getUtcTimestamp } from '../utils/date';
import '../styles/styles.css';
import FloatingChat from './FloatingChat';

const FlashcardList = ({ accessToken, supabaseRefreshToken, onSignIn }) => {
    const [flashcardData, setFlashcardData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [apiStatus, setApiStatus] = useState('');
    const [isRegenerating, setIsRegenerating] = useState(false);
    const [regenerateModalVisible, setRegenerateModalVisible] = useState(false);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
    const location = useLocation();
    const navigate = useNavigate();
    const document = location.state?.selectedDocument;
    const regeneratePromiseRef = useRef(null);
    const costConfirmationResolveRef = useRef(null);
    const [costConfirmation, setCostConfirmation] = useState(null);

    const startTimer = useCallback(() => null, []);

    const stopTimer = useCallback(() => {}, []);

    const handleRegenerationLoading = useCallback((value) => {
        setIsRegenerating(Boolean(value));
    }, []);

    useEffect(() => {
        return () => {
            if (costConfirmationResolveRef.current) {
                costConfirmationResolveRef.current(false);
                costConfirmationResolveRef.current = null;
            }
        };
    }, []);

    const fetchFlashcards = useCallback(async ({ showSpinner = false } = {}) => {
        if (!document) {
            setError('No document selected.');
            if (showSpinner) {
                setLoading(false);
            } else {
                setRefreshing(false);
            }
            return;
        }

        setError(null);
        if (showSpinner) {
            setLoading(true);
        } else {
            setRefreshing(true);
        }

        let authToken = accessToken;

        try {
            const flashcardUrl = `${QUIZMASTER_API}/get-flashcards-by-document/?document_id=${document.id}`;
            let flashcardResponse = await fetch(flashcardUrl, {
                headers: { 'Accept': 'application/json', 'Authorization': `Bearer ${authToken}` },
            });

            if (!flashcardResponse.ok) {
                if (flashcardResponse.status === 401 && supabaseRefreshToken) {
                    const refreshResponse = await fetch(`${QUIZMASTER_API}/auth/refresh`, {
                        method: 'POST',
                        headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
                        body: JSON.stringify({ refresh_token: supabaseRefreshToken }),
                    });
                    if (!refreshResponse.ok) {
                        throw new Error('Token refresh failed');
                    }
                    const { access_token: newAccessToken, refresh_token: newRefreshToken } = await refreshResponse.json();
                    onSignIn({ user_id: document.user_id, accessToken: newAccessToken, refreshToken: newRefreshToken });
                    authToken = newAccessToken;
                    flashcardResponse = await fetch(flashcardUrl, {
                        headers: { 'Accept': 'application/json', 'Authorization': `Bearer ${authToken}` },
                    });
                    if (!flashcardResponse.ok) {
                        throw new Error('Failed to fetch flashcards after token refresh');
                    }
                } else {
                    throw new Error('Failed to fetch flashcards');
                }
            }

            const flashcardsData = await flashcardResponse.json();
            const flashcards = flashcardsData.flashcards || [];

            if (flashcards.length === 0) {
                setFlashcardData([]);
                return;
            }

            setFlashcardData(flashcards);
        } catch (err) {
            console.error('[FlashcardList] Error fetching flashcards:', err);
            setError(err.message);
        } finally {
            if (showSpinner) {
                setLoading(false);
            } else {
                setRefreshing(false);
            }
        }
    }, [document, accessToken, supabaseRefreshToken, onSignIn]);

    useEffect(() => {
        fetchFlashcards({ showSpinner: true });
    }, [fetchFlashcards]);

    const refreshAfterRegen = useCallback(() => {
        fetchFlashcards({ showSpinner: false });
    }, [fetchFlashcards]);

    const filteredFlashcards = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        if (!term) {
            return flashcardData;
        }

        return flashcardData.filter((flashcard) => {
            const tokens = [
                flashcard.front_text,
                flashcard.back_text,
                flashcard.difficulty,
                flashcard.id,
            ];

            return tokens.some((value) =>
                value && String(value).toLowerCase().includes(term)
            );
        });
    }, [flashcardData, searchTerm]);

    const sortedFlashcards = useMemo(() => {
        const { key, direction } = sortConfig;
        if (!key) {
            return filteredFlashcards;
        }

        const multiplier = direction === 'asc' ? 1 : -1;

        return [...filteredFlashcards].sort((a, b) => {
            switch (key) {
                case 'difficulty':
                    return multiplier * String(a.difficulty || '').localeCompare(String(b.difficulty || ''));
                case 'front_text':
                    return multiplier * String(a.front_text || '').localeCompare(String(b.front_text || ''));
                case 'created_at':
                default: {
                    const aDate = getUtcTimestamp(a.created_at);
                    const bDate = getUtcTimestamp(b.created_at);
                    return multiplier * ((aDate ?? 0) - (bDate ?? 0));
                }
            }
        });
    }, [filteredFlashcards, sortConfig]);

    const handleSort = useCallback((key) => {
        setSortConfig((prev) => {
            if (prev.key === key) {
                return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
            }

            const defaultDirection = key === 'created_at' ? 'desc' : 'asc';
            return { key, direction: defaultDirection };
        });
    }, []);

    const getSortIcon = useCallback((key) => {
        if (sortConfig.key !== key) {
            return <FaSort size={12} />;
        }
        return sortConfig.direction === 'asc' ? <FaSortUp size={12} /> : <FaSortDown size={12} />;
    }, [sortConfig]);

    const formatDate = useCallback(
        (dateStr) =>
            formatToLocalDateTime(dateStr, {
                fallback: 'N/A',
                includeSeconds: false,
                dateStyle: 'medium',
                timeStyle: 'short',
                timeZoneName: null,
            }),
        []
    );

    const getDifficultyBadgeClass = useCallback((difficulty) => {
        const normalized = (difficulty || '').toLowerCase();
        if (normalized === 'easy') return 'flashcard-card__difficulty--easy';
        if (normalized === 'medium') return 'flashcard-card__difficulty--medium';
        if (normalized === 'hard') return 'flashcard-card__difficulty--hard';
        return 'flashcard-card__difficulty--default';
    }, []);

    const handleRefreshClick = useCallback(() => {
        fetchFlashcards({ showSpinner: false });
    }, [fetchFlashcards]);

    const confirmRegenerationCost = useCallback(({ estimatedPoints, numFlashcards }) => {
        return new Promise((resolve) => {
            if (costConfirmationResolveRef.current) {
                costConfirmationResolveRef.current(false);
            }
            costConfirmationResolveRef.current = resolve;
            setCostConfirmation({ estimatedPoints, numFlashcards });
        });
    }, []);

    const handleCostModalConfirm = useCallback(() => {
        setCostConfirmation(null);
        if (costConfirmationResolveRef.current) {
            costConfirmationResolveRef.current(true);
            costConfirmationResolveRef.current = null;
        }
    }, []);

    const handleCostModalCancel = useCallback(() => {
        setCostConfirmation(null);
        if (costConfirmationResolveRef.current) {
            costConfirmationResolveRef.current(false);
            costConfirmationResolveRef.current = null;
        }
    }, []);

    const handleRegenerateFlashcards = useCallback(async () => {
        if (!document) {
            setError('No document selected.');
            return;
        }
        try {
            setRegenerateModalVisible(true);
            const { maxFlashcards, enableOcr, difficulty } = await new Promise((resolve, reject) => {
                regeneratePromiseRef.current = { resolve, reject };
            });
            console.log('[FlashcardList] Calling regenerateFlashcards with difficulty:', difficulty);
            await regenerateFlashcards({
                documentId: document.id,
                tokenCount: document.token_count,
                filename: document.filename,
                setApiStatus,
                startTimer,
                stopTimer,
                setLoading: handleRegenerationLoading,
                refreshDocuments: refreshAfterRegen,
                accessToken,
                supabaseRefreshToken,
                onSignIn,
                maxFlashcards,
                enableOcr,
                difficulty,
                confirmCost: confirmRegenerationCost,
            });
        } catch (error) {
            if (error.message !== 'User canceled') {
                console.error('[FlashcardList] Error regenerating flashcards:', error);
                setApiStatus(error.message || 'An error occurred while regenerating the flashcards.');
            }
        }
    }, [
        document,
        accessToken,
        supabaseRefreshToken,
        onSignIn,
        startTimer,
        stopTimer,
        handleRegenerationLoading,
        refreshAfterRegen,
        confirmRegenerationCost,
    ]);

    if (loading) {
        return (
            <div className="loading-container">
                <div className="spinner"></div>
                <p>Loading flashcards...</p>
            </div>
        );
    }

    if (error || !document) {
        return (
            <div className="error-container">
                <p>{error || 'No document selected.'}</p>
                <button className="button confirm-button" onClick={() => navigate('/')}>
                    Back to Home
                </button>
            </div>
        );
    }

    const statusSegments = [];
    if (apiStatus) {
        statusSegments.push(apiStatus);
    }
    if (refreshing) {
        statusSegments.push('Refreshing flashcard list...');
    }
    const statusMessage = statusSegments.join(' | ') || (isRegenerating ? 'Working on your flashcards...' : '');

    const hasFlashcards = Array.isArray(flashcardData) && flashcardData.length > 0;

    return (
        <div className="scroll-container">
            <div className="document-list-wrapper flashcard-list-wrapper">
                <div className="quiz-list-header">
                    <div className="quiz-list-header__titles">
                        <h2 className="sub-header">Flashcards for {document.filename}</h2>
                        <p className="quiz-list-header__subtitle">
                            Study and review your flashcards. Click on any card to start reviewing.
                        </p>
                    </div>
                </div>

                {statusMessage && (
                    <div className="api-status-container">
                        {(isRegenerating || refreshing) && <div className="spinner spinner--inline" aria-hidden="true"></div>}
                        <p className="api-status-text">{statusMessage}</p>
                    </div>
                )}

                <div className="search-container quiz-list-search">
                    <div className="search-input-wrapper">
                        <FaSearch className="search-icon" aria-hidden="true" />
                        <input
                            type="text"
                            className="search-input search-input--large"
                            placeholder="Search by question, answer, or difficulty..."
                            aria-label="Search flashcards"
                            value={searchTerm}
                            onChange={(event) => setSearchTerm(event.target.value)}
                        />
                        {searchTerm && (
                            <button
                                type="button"
                                className="clear-button"
                                onClick={() => setSearchTerm('')}
                                title="Clear search"
                                aria-label="Clear search"
                            >
                                <FaTimes className="clear-button-icon" aria-hidden="true" />
                            </button>
                        )}
                    </div>
                    <button
                        type="button"
                        className="button button--icon quiz-regenerate-button"
                        onClick={handleRegenerateFlashcards}
                        disabled={loading || refreshing || isRegenerating}
                        title="Generate fresh AI-powered flashcards for this document"
                        aria-label="Regenerate new flashcards"
                    >
                        <span className="quiz-regenerate-button__icon" aria-hidden="true">
                            <FaMagic />
                        </span>
                        <span className="quiz-regenerate-button__badge" aria-hidden="true">NEW</span>
                    </button>
                </div>

                <div className="document-list-container">
                    <div className="document-list-controls quiz-list-controls">
                        <div className="document-sort">
                            <span className="document-sort__label">Sort by</span>
                            <button
                                type="button"
                                className="document-sort-button"
                                onClick={() => handleSort('created_at')}
                                aria-pressed={sortConfig.key === 'created_at'}
                            >
                                Most Recent {getSortIcon('created_at')}
                            </button>
                            <button
                                type="button"
                                className="document-sort-button"
                                onClick={() => handleSort('difficulty')}
                                aria-pressed={sortConfig.key === 'difficulty'}
                            >
                                Difficulty {getSortIcon('difficulty')}
                            </button>
                            <button
                                type="button"
                                className="document-sort-button"
                                onClick={() => handleSort('front_text')}
                                aria-pressed={sortConfig.key === 'front_text'}
                            >
                                Question {getSortIcon('front_text')}
                            </button>
                        </div>
                        <button
                            type="button"
                            className="button button--ghost quiz-refresh-button"
                            onClick={handleRefreshClick}
                            disabled={refreshing || loading || isRegenerating}
                        >
                            <FaSync aria-hidden="true" />
                            <span>{refreshing ? 'Refreshing...' : 'Refresh List'}</span>
                        </button>
                    </div>

                    {hasFlashcards ? (
                        <div className="document-grid quiz-grid">
                            {sortedFlashcards.map((flashcard) => {
                                const difficultyClass = getDifficultyBadgeClass(flashcard.difficulty);

                                return (
                                    <div key={flashcard.id} className="document-card quiz-card flashcard-card">
                                        <div className="quiz-card__header">
                                            <div className="quiz-card__title-group">
                                                <h3 className="quiz-card__title">{flashcard.front_text}</h3>
                                                <span className={`quiz-card__status ${difficultyClass}`}>{flashcard.difficulty}</span>
                                            </div>
                                            <FaArrowRight aria-hidden="true" className="document-card__icon" />
                                        </div>
                                        <div className="quiz-card__meta">
                                            <span className="quiz-card__label">Answer Preview</span>
                                            <span className="quiz-card__value">{flashcard.back_text.substring(0, 50)}...</span>
                                        </div>
                                        <div className="quiz-card__meta">
                                            <span className="quiz-card__label">Created</span>
                                            <span className="quiz-card__value">{formatDate(flashcard.created_at)}</span>
                                        </div>
                                        <div className="quiz-card__actions">
                                            <button
                                                className="button quiz-card__button"
                                                onClick={() => {
                                                    navigate('/flashcard', {
                                                        state: {
                                                            selectedFlashcardId: flashcard.id,
                                                            selectedDocument: document,
                                                            flashcards: flashcardData,
                                                        },
                                                    });
                                                }}
                                            >
                                                Review Flashcard
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="document-empty-state quiz-empty-state">
                            {searchTerm
                                ? 'No flashcards match your search.'
                                : `No flashcards found for ${document.filename}. Use "Regenerate Flashcards" to create new flashcards.`}
                        </div>
                    )}
                </div>

                <div className="quiz-list-footer">
                    <button className="button" onClick={() => navigate('/')} >
                        Back to Documents
                    </button>
                </div>
            </div>

            <FlashcardOptionsModal
                visible={regenerateModalVisible}
                onConfirm={(options) => {
                    setRegenerateModalVisible(false);
                    regeneratePromiseRef.current?.resolve(options);
                    regeneratePromiseRef.current = null;
                }}
                onCancel={() => {
                    setRegenerateModalVisible(false);
                    regeneratePromiseRef.current?.reject(new Error('User canceled'));
                    regeneratePromiseRef.current = null;
                }}
              />
              <PointsConfirmationModal
                  visible={Boolean(costConfirmation)}
                  estimatedPoints={costConfirmation?.estimatedPoints ?? 0}
                  numQuestions={costConfirmation?.numFlashcards ?? 0}
                  onConfirm={handleCostModalConfirm}
                  onCancel={handleCostModalCancel}
                  title="Confirm flashcard regeneration"
                  confirmText="Yes, regenerate"
                  questionText="flashcards"
            />
            {document && (
                <FloatingChat
                    documentId={document.id}
                    filename={document.filename}
                    accessToken={accessToken}
                />
            )}
        </div>
    );
};

export default FlashcardList;

